import { createStore, createTypedHooks } from 'easy-peasy';
import { aiCreateTipModel, IAICreateTipModel } from '@/editor/components/ai-create-tip/models';
import { IImageAdjustModel, imagePanelAdjustModel } from '@/store/panel-models/image-panel-adjust-model';
import { adjustCollapseModel, IAdjustCollapseModel } from './collapse-models/adjust-collapse-model';
import { brushCollapseModel, IBrushCollapseModel } from './collapse-models/brush-collapse-model';
import { collapseModel, ICollapseModel } from './collapse-models/collapse-model';
import { elementCollapseModel, IElementCollapseModel } from './collapse-models/element-collapse-model';
import { IMaterialCollapse, materialCollapseModel } from './collapse-models/material-collapse-model';
import { IMineCollapse, mineCollapseModel } from './collapse-models/mine-collapse-model';
import { IMogCollapseModel, mogCollapseModel } from './collapse-models/mog-collapse-model';
import { INavigationCollapseModel, navigationCollapseModel } from './collapse-models/navigation-collapse-model';
import { IStageSizeCollapse, stageSizeCollapseModel } from './collapse-models/stage-size-collapse-model';
import { ITemplateCollapse, templateCollapseModel } from './collapse-models/template-collapse-model';
import { IWatermarkCollapseModel, watermarkCollapseModel } from './collapse-models/watermark-collapse-model';
import { aiMaterialModel, IAIMaterialModel } from './models/ai-material-model';
import { aiPPTModel, IAiPPTModel } from './models/ai-ppt-model';
import { askerInfoModel, IAskerInfoModel } from './models/asker-info-model';
import { cutoutModel, ICutoutModel } from './models/cutout-model';
import { cutoutVipModel, ICutoutVipModel } from './models/cutout-vip-model';
import { editorModel, IEditorModel } from './models/editor-model';
import { extensionModel, IExtensionModel } from './models/extensions-model';
import { fontMaterialModel, IFontMaterialModel } from './models/font-material-model';
import { IImageEditorModel, imageEditorModel } from './models/image-editor-model';
import { IInitModel, initModel } from './models/init-model';
import { IMeidouModel, meidouModel } from './models/meidou-model';
import { IPluginModel, pluginModel } from './models/plugin-model';
import { IPPTCreateTipModel, pptCreateTipModel } from './models/ppt-create-tip-model';
import { IRecordType, recordModel } from './models/record-model';
import { ISaveInfoModel, saveInfoModel } from './models/save-model';
import { ISdkModel, sdkModel } from './models/sdk-model';
import { ISearchInfoModel, searchModel } from './models/search-model';
import { ISvgModel, svgModel } from './models/svg-model';
import { ISvipWatermarkModel, svipWatermarkModel } from './models/svip-water-mask-model';
import { ITeamFontsModel, teamFontsModel } from './models/team-fonts-model';
import { ITeamModel, teamModel } from './models/team-model';
import { ITemplateModel, templateModel } from './models/template-model';
import { ITextMarkModel, textMarkModel } from './models/text-mark-model';
import { IUserInfoModel, userInfoModel } from './models/user-info-model';

interface IAppStore {
    pptCreateTipModel: IPPTCreateTipModel;
    adjustCollapseModel: IAdjustCollapseModel;
    collapseModel: ICollapseModel;
    elementCollapseModel: IElementCollapseModel;
    materialCollapseModel: IMaterialCollapse;
    mineCollapseModel: IMineCollapse;
    mogCollapseModel: IMogCollapseModel;
    navigationCollapseModel: INavigationCollapseModel;
    stageSizeCollapseModel: IStageSizeCollapse;
    templateCollapseModel: ITemplateCollapse;
    brushCollapseModel: IBrushCollapseModel;
    watermarkCollapseModel: IWatermarkCollapseModel;
    aiMaterialModel: IAIMaterialModel;
    aiPPTModel: IAiPPTModel;
    cutoutModel: ICutoutModel;
    cutoutVipModel: ICutoutVipModel;
    editorModel: IEditorModel;
    extensionModel: IExtensionModel;
    imageEditorModel: IImageEditorModel;
    initModel: IInitModel;
    meidouModel: IMeidouModel;
    pluginModel: IPluginModel;
    recordModel: IRecordType;
    saveInfoModel: ISaveInfoModel;
    sdkModel: ISdkModel;
    searchModel: ISearchInfoModel;
    teamFontsModel: ITeamFontsModel;
    teamModel: ITeamModel;
    templateModel: ITemplateModel;
    userInfoModel: IUserInfoModel;
    svipWatermarkModel: ISvipWatermarkModel;
    fontMaterialModel: IFontMaterialModel;
    imagePanelAdjustModel: IImageAdjustModel;
    aiCreateTipModel: IAICreateTipModel;
    askerInfoModel: IAskerInfoModel;
    textMarkModel: ITextMarkModel;
    svgModel: ISvgModel;
}

let store: ReturnType<typeof createStore<IAppStore>>;

const initStore = () => {
    store = createStore<IAppStore>({
        pptCreateTipModel,
        adjustCollapseModel,
        collapseModel,
        elementCollapseModel,
        materialCollapseModel,
        mineCollapseModel,
        mogCollapseModel,
        navigationCollapseModel,
        stageSizeCollapseModel,
        templateCollapseModel,
        brushCollapseModel,
        watermarkCollapseModel,
        aiMaterialModel,
        aiPPTModel,
        cutoutModel,
        cutoutVipModel,
        extensionModel,
        editorModel,
        imageEditorModel,
        initModel,
        meidouModel,
        pluginModel,
        recordModel,
        saveInfoModel,
        sdkModel,
        searchModel,
        teamFontsModel,
        teamModel,
        templateModel,
        userInfoModel,
        svipWatermarkModel,
        fontMaterialModel,
        imagePanelAdjustModel,
        aiCreateTipModel,
        askerInfoModel,
        textMarkModel,
        svgModel,
    });

    return store;
};

const typedHooks = createTypedHooks<IAppStore>();
export const useStoreState = typedHooks.useStoreState;
export const useStoreActions = typedHooks.useStoreActions;
export const useStoreDispatch = typedHooks.useStoreDispatch;

export default function getStore() {
    if (!store) return initStore();

    return store;
}
